// ─── Progressive Pergola — Sun Position Simulator ────────────────────────────
// Pure logic module — no DOM, no Three.js dependencies.
// Converts SunCalc output to Three.js directional light parameters.

import SunCalc from 'suncalc';

// ─── Constants ───────────────────────────────────────────────────────────────

export const PERGOLA_FACINGS = [
  { label: 'N',  degrees: 0   },
  { label: 'NE', degrees: 45  },
  { label: 'E',  degrees: 90  },
  { label: 'SE', degrees: 135 },
  { label: 'S',  degrees: 180 },  // default — front faces south
  { label: 'SW', degrees: 225 },
  { label: 'W',  degrees: 270 },
  { label: 'NW', degrees: 315 },
];

// Default: Verona NJ (Breslow Home Designs service area)
const DEFAULT_LAT   = 40.83;
const DEFAULT_LNG   = -74.24;
const LIGHT_RADIUS  = 20;    // distance from origin
const MAX_INTENSITY = 1.2;   // peak intensity at solar zenith

// ─── SunSimulator ─────────────────────────────────────────────────────────────

export class SunSimulator {
  constructor() {
    this.lat           = DEFAULT_LAT;
    this.lng           = DEFAULT_LNG;
    this.date          = new Date();
    this.timeMinutes   = 960;   // 4:00 PM default — peak afternoon use window
    this.pergolaFacing = 180;   // degrees from North (180 = front faces south)
    this.enabled       = false;
    this.playing       = false;
    this.manualAzimuth = null;  // null = SunCalc; degrees 0–360 (N-CW) when user drags sun
  }

  // ─── Setters ───────────────────────────────────────────────────────────────

  setLocation(lat, lng) {
    this.lat = lat;
    this.lng = lng;
  }

  // dateStr: 'YYYY-MM-DD'  — parsed as local time to avoid UTC offset issues
  setDateFromString(dateStr) {
    const [y, m, d] = dateStr.split('-').map(Number);
    this.date = new Date(y, m - 1, d);
  }

  setTimeMinutes(t) {
    this.timeMinutes = Math.max(0, Math.min(1439, t));
  }

  setPergolaFacing(degrees) {
    this.pergolaFacing = degrees;
  }

  // Called each animation frame when playing
  advanceTimeMinutes(delta) {
    this.timeMinutes = (this.timeMinutes + delta) % 1440;
  }

  // ─── Core calculation ──────────────────────────────────────────────────────
  //
  // Coordinate system in Three.js (pergola convention):
  //   +X = right side of pergola    −X = left side
  //   +Z = back of pergola          −Z = front of pergola
  //   +Y = up
  //
  // A DirectionalLight at world position P illuminates faces whose normals
  // point toward P (dot(normal, P) > 0).
  //
  // Mapping (default south-facing pergola, pergolaFacing = 180°):
  //   South sun  → light at (0, y, −R)  → illuminates front face (−Z normal) ✓
  //   East sun   → light at (+R, y, 0)  → illuminates right face (+X normal) ✓
  //   North sun  → light at (0, y, +R)  → illuminates back face  (+Z normal) ✓
  //   West sun   → light at (−R, y, 0)  → illuminates left face  (−X normal) ✓
  //
  // The pergolaFacing offset rotates the apparent sun path relative to the
  // pergola so the "front" face is always the one the pergola actually faces:
  //   bearing = (azNorthCW − facingRad + π) mod 2π
  //   With default 180°:  bearing = azNorthCW  (no net change)
  //   East-facing (90°):  east sun → bearing 180° → light at −Z → front lit ✓

  _buildDate() {
    const d = new Date(this.date);
    d.setHours(Math.floor(this.timeMinutes / 60), this.timeMinutes % 60, 0, 0);
    return d;
  }

  getLightParams() {
    const pos      = SunCalc.getPosition(this._buildDate(), this.lat, this.lng);
    const altitude = pos.altitude;   // radians; negative = below horizon

    // Night — sun below horizon
    if (altitude <= 0) {
      return {
        x: 10, y: 0.5, z: 8,
        intensity: 0.0,
        color: 0x101020,
        altitude: altitude * (180 / Math.PI),
        azimuthDeg: 0,
        isNight: true,
      };
    }

    // SunCalc azimuth: from South, CW (South=0, West=π/2, North=±π, East=−π/2)
    // → add π to shift reference to North, CW; normalize to [0, 2π)
    const azNorthCW = (pos.azimuth + Math.PI + 2 * Math.PI) % (2 * Math.PI);

    // Manual azimuth override: user dragged the sun dot on the compass.
    // Altitude still comes from SunCalc; only the horizontal direction is overridden.
    const effectiveAz = this.manualAzimuth !== null
      ? this.manualAzimuth * (Math.PI / 180)
      : azNorthCW;

    const bearing = effectiveAz;

    const cosAlt = Math.cos(altitude);
    const x = Math.sin(bearing) * cosAlt * LIGHT_RADIUS;
    const z = Math.cos(bearing) * cosAlt * LIGHT_RADIUS;
    const y = Math.sin(altitude) * LIGHT_RADIUS;

    // Intensity: quick ramp from 0 at horizon to MAX at zenith
    const t = altitude / (Math.PI / 2);
    const intensity = MAX_INTENSITY * Math.pow(t, 0.6);

    // Color: warm orange near horizon (altitude < 15°) → warm white at midday (> 40°)
    const colorT = Math.min(1.0, Math.max(0.0, (altitude * (180 / Math.PI) - 15) / 25));
    const r = 0xff;
    const g = Math.round(0x8c + (0xf4 - 0x8c) * colorT);
    const b = Math.round(0x00 + (0xe0 - 0x00) * colorT);
    const color = (r << 16) | (g << 8) | b;

    return {
      x, y, z,
      intensity,
      color,
      altitude:    altitude * (180 / Math.PI),
      azimuthDeg:  effectiveAz * (180 / Math.PI),
      isNight:     false,
    };
  }

  // ─── Display helpers ──────────────────────────────────────────────────────

  getTimeDisplay() {
    const h    = Math.floor(this.timeMinutes / 60);
    const m    = this.timeMinutes % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 === 0 ? 12 : h % 12;
    return `${displayH}:${String(Math.floor(m)).padStart(2, '0')} ${ampm}`;
  }

  getDateInputValue() {
    const y = this.date.getFullYear();
    const m = String(this.date.getMonth() + 1).padStart(2, '0');
    const d = String(this.date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
